import { HttpClient } from '@/api/services/http';
import api from '@/services/api';
import {
    Content,
    ContentProcessingOptions,
    ContentIngestJobMeta,
    ContentIngestJobKind,
    ContentIngestEvent,
    ContentIngestErrorEvent,
    ContentIngestStatusEvent,
    ChatbotConnection,
    ContentListMeta,
    ContentListResult,
    ContentVersion,
    ContentComment,
    ContentTag,
    User
} from '@/types/content';
const API_URL = '/chatbot-document';

const DEFAULT_PROCESSING_OPTIONS = {
    chunk_size: 800,
    chunk_overlap: 80,
    content_type_hint: 'narrative'
};

const ingestEventSources = new Map<string, EventSource>();

interface RawIngestResponse {
    success?: boolean;
    message?: string;
    job_id?: string;
    ingestion_job_id?: string;
    status?: string;
    stream_url?: string;
    stream_token?: string;
    chunks_indexed?: number;
    [key: string]: unknown;
}

type IngestStreamHandlers = {
    onOpen?: () => void;
    onEvent?: (event: ContentIngestEvent) => void;
    onComplete?: (event: ContentIngestEvent) => void;
    onError?: (event: ContentIngestEvent) => void;
    onHeartbeat?: (event: MessageEvent<string>) => void;
};

const buildProcessingOptions = (options?: ContentProcessingOptions) => {
    if (!options) {
        return { ...DEFAULT_PROCESSING_OPTIONS };
    }

    const processing = {
        chunk_size: options.chunkSize ?? DEFAULT_PROCESSING_OPTIONS.chunk_size,
        chunk_overlap: options.chunkOverlap ?? DEFAULT_PROCESSING_OPTIONS.chunk_overlap,
        content_type_hint: options.contentTypeHint ?? DEFAULT_PROCESSING_OPTIONS.content_type_hint
    };

    if (options.metadata && Object.keys(options.metadata).length > 0) {
        return {
            ...processing,
            metadata: options.metadata
        };
    }

    return processing;
};

const normalizeIdArray = (values?: (string | { id?: string | null })[]) => {
    if (!values) {
        return [];
    }

    return values
        .map(value => {
            if (typeof value === 'string') {
                return value && value !== 'all' ? value : null;
            }
            return value?.id ?? null;
        })
        .filter((id): id is string => Boolean(id));
};


const buildStreamUrl = (streamPath: string) => {
    if (!streamPath) {
        throw new Error('Missing stream URL in ingestion response');
    }

    // Stream URLs from the backend are expected to be absolute
    if (streamPath.startsWith('http://') || streamPath.startsWith('https://')) {
        return streamPath;
    }

    // Fallback: resolve relative paths against the main API base URL
    const apiBaseUrl = (import.meta.env.VITE_REACT_APP_API_URL as string || 'http://localhost:4000/api').replace(/\/api\/?$/, '');
    const normalizedPath = streamPath.startsWith('/') ? streamPath : `/${streamPath}`;
    return `${apiBaseUrl}${normalizedPath}`;
};

const buildStreamConnectionUrl = (streamUrl: string, streamToken?: string) => {
    if (!streamToken) {
        console.warn('ContentService: Missing stream token for SSE connection');
        return streamUrl;
    }

    const separator = streamUrl.includes('?') ? '&' : '?';
    return `${streamUrl}${separator}token=${encodeURIComponent(streamToken)}`;
};

const normalizeIngestResponse = (
    data: RawIngestResponse,
    type: ContentIngestJobKind,
    originalContent?: Partial<Content>
): ContentIngestJobMeta => {
    const jobId = data?.job_id ?? data?.ingestion_job_id;

    if (!jobId) {
        console.error('ContentService: Invalid ingest response payload', data);
        throw new Error('Ingestion service returned an invalid response');
    }

    // The Fastify proxy may complete ingestion synchronously (no stream_url).
    // In that case, mark the job as already completed.
    const hasStream = Boolean(data?.stream_url);

    return {
        jobId,
        streamPath: data.stream_url ?? '',
        streamUrl: hasStream ? buildStreamUrl(data.stream_url!) : '',
        streamToken: typeof data.stream_token === 'string' ? data.stream_token : undefined,
        status: typeof data.status === 'string' ? data.status : 'received',
        message: typeof data.message === 'string' ? data.message : undefined,
        type,
        submittedAt: new Date().toISOString(),
        originalContent
    };
};

interface FetchContentResponse {
    data: Content[];
}

const normalizeDate = (value?: string | null) => {
    if (!value) {
        return new Date().toISOString();
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        console.warn('ContentService: encountered invalid date value', value);
        return new Date().toISOString();
    }

    return date.toISOString();
};

const normalizeProcessingOptions = (options?: Record<string, unknown>) => {
    if (!options || typeof options !== 'object') {
        return undefined;
    }

    return {
        chunkSize: typeof options.chunk_size === 'number' ? options.chunk_size : undefined,
        chunkOverlap: typeof options.chunk_overlap === 'number' ? options.chunk_overlap : undefined,
        contentTypeHint: typeof options.content_type_hint === 'string' ? options.content_type_hint : undefined,
        metadata: typeof options.metadata === 'object' ? options.metadata as Record<string, unknown> : undefined
    };
};

const normalizeIdArrayFlexible = (value: unknown): (string | ChatbotConnection)[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    return value
        .map(item => {
            if (!item) return null;
            if (typeof item === 'string') {
                return item;
            }
            if (typeof item === 'object' && typeof (item as any).id === 'string') {
                return (item as { id: string; name?: string });
            }
            return null;
        })
        .filter((item): item is string | ChatbotConnection => item !== null);
};

const buildFallbackId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const normalizeUser = (value: unknown, fallbackPrefix = 'user'): User | undefined => {
    if (!value) {
        return undefined;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return undefined;
        }
        const isEmail = trimmed.includes('@');
        const name = isEmail ? trimmed.split('@')[0] || trimmed : trimmed;
        return {
            id: trimmed,
            name,
            email: isEmail ? trimmed : undefined
        };
    }

    if (typeof value !== 'object') {
        return undefined;
    }

    const raw = value as Record<string, unknown>;
    const id =
        (typeof raw.id === 'string' && raw.id.trim()) ||
        (typeof raw.user_id === 'string' && raw.user_id.trim()) ||
        (typeof raw.email === 'string' && raw.email.trim()) ||
        undefined;
    const name =
        (typeof raw.name === 'string' && raw.name.trim()) ||
        (typeof raw.full_name === 'string' && raw.full_name.trim()) ||
        (typeof raw.displayName === 'string' && raw.displayName.trim()) ||
        (typeof raw.email === 'string' && raw.email.split('@')[0]) ||
        undefined;

    const email = typeof raw.email === 'string' ? raw.email : undefined;
    const avatar =
        (typeof raw.avatar === 'string' && raw.avatar) ||
        (typeof raw.image === 'string' && raw.image) ||
        (typeof raw.avatar_url === 'string' && raw.avatar_url) ||
        undefined;
    const role =
        (typeof raw.role === 'string' && raw.role) ||
        (typeof raw.user_role === 'string' && raw.user_role) ||
        undefined;

    return {
        id: id ?? buildFallbackId(fallbackPrefix),
        name: name ?? 'Unknown',
        email,
        avatar,
        role
    };
};

const normalizeUserArray = (value: unknown, fallbackPrefix = 'user'): User[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const users = value
        .map(item => normalizeUser(item, fallbackPrefix))
        .filter((user): user is User => Boolean(user));

    return users.length ? users : undefined;
};

const normalizeTagValue = (value: unknown): string | ContentTag | null => {
    if (typeof value === 'string') {
        return value;
    }

    if (!value || typeof value !== 'object') {
        return null;
    }

    const raw = value as Record<string, unknown>;
    const id =
        (typeof raw.id === 'string' && raw.id.trim()) ||
        (typeof raw.tag_id === 'string' && raw.tag_id.trim()) ||
        undefined;
    const name =
        (typeof raw.name === 'string' && raw.name.trim()) ||
        (typeof raw.label === 'string' && raw.label.trim()) ||
        id ||
        undefined;
    const color =
        (typeof raw.color === 'string' && raw.color) ||
        (typeof raw.hex === 'string' && raw.hex) ||
        '#6b7280';

    return {
        id: id ?? buildFallbackId('tag'),
        name: name ?? 'Tag',
        color
    };
};

const normalizeTags = (value: unknown): (string | ContentTag)[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const tags = value
        .map(tag => normalizeTagValue(tag))
        .filter((tag): tag is string | ContentTag => Boolean(tag));

    return tags.length ? tags : undefined;
};

const normalizeVersions = (value: unknown, contentId?: string): ContentVersion[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const versions = value
        .map(item => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const raw = item as Record<string, unknown>;
            const createdBy =
                normalizeUser(raw.createdBy ?? raw.created_by ?? raw.author, 'version-user') ??
                {
                    id: buildFallbackId('user'),
                    name: 'Unknown'
                };

            return {
                id:
                    (typeof raw.id === 'string' && raw.id) ||
                    (typeof raw.version_id === 'string' && raw.version_id) ||
                    buildFallbackId('version'),
                contentId:
                    (typeof raw.contentId === 'string' && raw.contentId) ||
                    (typeof raw.content_id === 'string' && raw.content_id) ||
                    contentId ||
                    buildFallbackId('content'),
                content: typeof raw.content === 'string' ? raw.content : '',
                createdAt: normalizeDate(raw.created_at ?? raw.createdAt),
                createdBy,
                changes:
                    (typeof raw.changes === 'string' && raw.changes) ||
                    (typeof raw.description === 'string' && raw.description) ||
                    ''
            } as ContentVersion;
        })
        .filter((version): version is ContentVersion => Boolean(version));

    return versions.length ? versions : undefined;
};

const normalizeComments = (value: unknown, contentId?: string): ContentComment[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const comments = value
        .map(item => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const raw = item as Record<string, unknown>;
            const createdBy =
                normalizeUser(raw.createdBy ?? raw.created_by ?? raw.author, 'comment-user') ??
                {
                    id: buildFallbackId('user'),
                    name: 'Unknown'
                };

            const text =
                (typeof raw.text === 'string' && raw.text) ||
                (typeof raw.content === 'string' && raw.content) ||
                '';

            return {
                id:
                    (typeof raw.id === 'string' && raw.id) ||
                    (typeof raw.comment_id === 'string' && raw.comment_id) ||
                    buildFallbackId('comment'),
                contentId:
                    (typeof raw.contentId === 'string' && raw.contentId) ||
                    (typeof raw.content_id === 'string' && raw.content_id) ||
                    contentId ||
                    buildFallbackId('content'),
                text,
                createdAt: normalizeDate(raw.created_at ?? raw.createdAt),
                createdBy
            } as ContentComment;
        })
        .filter((comment): comment is ContentComment => Boolean(comment));

    return comments.length ? comments : undefined;
};

const normalizeMetadataRecord = (value: unknown): Record<string, unknown> | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    return value as Record<string, unknown>;
};

const normalizeContentRecord = (raw: any): Content => {
    if (!raw || typeof raw !== 'object') {
        throw new Error('Invalid content record received from ingestion service');
    }

    const recordId =
        (typeof raw.id === 'string' && raw.id) ||
        (typeof raw.content_id === 'string' && raw.content_id) ||
        (typeof raw.document_id === 'string' && raw.document_id) ||
        undefined;

    const chatbotsSource = raw.chatbots ?? raw.connected_chatbots ?? raw.chatbotConnections;
    const copilotsSource = raw.copilots ?? raw.connected_copilots ?? raw.copilotConnections;
    const chatbots = normalizeIdArrayFlexible(chatbotsSource);
    const copilots = normalizeIdArrayFlexible(copilotsSource) as (string | ChatbotConnection)[] | undefined;
    const folderId =
        (typeof raw.folder_id === 'string' && raw.folder_id) ||
        (typeof raw.folderId === 'string' && raw.folderId) ||
        (raw.folder && typeof raw.folder.id === 'string' && raw.folder.id) ||
        undefined;
    const author = normalizeUser(raw.author ?? raw.created_by ?? raw.owner, 'author');
    const sharedWith = normalizeUserArray(raw.shared_with ?? raw.sharedWith, 'shared-user');
    const lastEditedBy = normalizeUser(raw.last_edited_by ?? raw.lastEditedBy, 'editor');
    const versions = normalizeVersions(raw.versions ?? raw.version_history, recordId);
    const comments = normalizeComments(raw.comments ?? raw.comment_history, recordId);
    const metadata = normalizeMetadataRecord(raw.metadata ?? raw.meta);
    const tags = normalizeTags(raw.tags ?? raw.tag_list);

    const normalized: Content = {
        id: recordId,
        title: raw.title ?? raw.name ?? 'Untitled',
        content:
            (typeof raw.content === 'string' && raw.content) ||
            (typeof raw.body === 'string' && raw.body) ||
            (typeof raw.url === 'string' && raw.url) ||
            '',
        type: (raw.type ?? raw.content_type ?? raw.contentType ?? 'snippet') as Content['type'],
        status: (raw.status ?? raw.state ?? 'processing') as Content['status'],
        createdAt: normalizeDate(raw.created_at ?? raw.createdAt ?? raw.createdOn),
        updatedAt: normalizeDate(raw.updated_at ?? raw.updatedAt ?? raw.modifiedAt),
        description: raw.description ?? undefined,
        category: raw.category ?? undefined,
        folderId,
        lastUpdated: raw.last_updated ? normalizeDate(raw.last_updated) : undefined,
        author: author ?? (typeof raw.author === 'string' ? raw.author : undefined),
        messageCount:
            typeof raw.message_count === 'number'
                ? raw.message_count
                : typeof raw.messageCount === 'number'
                    ? raw.messageCount
                    : typeof raw.stats?.message_count === 'number'
                        ? raw.stats.message_count
                        : undefined,
        tags,
        chatbots: chatbots ?? undefined,
        copilots: copilots ?? undefined,
        sharedWith,
        versions,
        comments,
        lastEditedBy,
        summary: typeof raw.summary === 'string' ? raw.summary : undefined,
        previewUrl: typeof raw.preview_url === 'string' ? raw.preview_url : typeof raw.previewUrl === 'string' ? raw.previewUrl : undefined,
        externalUrl: typeof raw.external_url === 'string' ? raw.external_url : typeof raw.externalUrl === 'string' ? raw.externalUrl : undefined,
        processingOptions: normalizeProcessingOptions(raw.processing_options ?? raw.processingOptions),
        metadata
    };

    return normalized;
};

const normalizeContentListResponse = (payload: any) => {
    const itemsArray = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
                ? payload
                : [];

    const items = itemsArray.map(normalizeContentRecord);

    const rawMeta = payload?.meta ?? payload;
    const meta = {
        total: typeof rawMeta?.total === 'number' ? rawMeta.total : items.length,
        page: typeof rawMeta?.page === 'number' ? rawMeta.page : undefined,
        limit: typeof rawMeta?.limit === 'number' ? rawMeta.limit : undefined,
        hasNext: Boolean(rawMeta?.has_next ?? rawMeta?.hasNext),
        hasPrev: Boolean(rawMeta?.has_prev ?? rawMeta?.hasPrev),
        textTotal: typeof (rawMeta?.text_total ?? rawMeta?.textTotal) === 'number' ? (rawMeta.text_total ?? rawMeta.textTotal) : undefined,
        fileTotal: typeof (rawMeta?.file_total ?? rawMeta?.fileTotal) === 'number' ? (rawMeta.file_total ?? rawMeta.fileTotal) : undefined,
        urlTotal: typeof (rawMeta?.url_total ?? rawMeta?.urlTotal) === 'number' ? (rawMeta.url_total ?? rawMeta.urlTotal) : undefined
    };

    return { items, meta } as ContentListResult;
};

export const contentService = {
    async createSnippet(content: Content): Promise<ContentIngestJobMeta> {
        const folderId = content.folderId && content.folderId !== 'all' ? content.folderId : null;
        const tags = content.tags ?? [];
        const chatbotIds = normalizeIdArray(content.chatbots);
        const copilotIds = (content.copilots ?? []).filter((id): id is string => Boolean(id));

        // Transform to new API payload structure
        const ingestPayload = {
            content: content.content,
            title: content.title,
            tags_id: tags,
            folder_id: folderId,
            chatbot_id: chatbotIds,
            copilot_id: copilotIds,
            metadata: {
                source: "manual_upload",
                category: content.description || "documentation"
            },
            processing_options: buildProcessingOptions(content.processingOptions)
        };

        console.log('ContentService: Creating snippet with ingest API', { ingestPayload });

        try {
            const response = await HttpClient.apiClient.post<RawIngestResponse>('/ingest/snippet', ingestPayload);
            const jobMeta = normalizeIngestResponse(response.data, 'text', {
                title: content.title,
                description: content.description,
                folderId: folderId ?? undefined,
                tags,
                chatbots: chatbotIds,
                copilots: copilotIds,
                type: 'snippet'
            });

            return jobMeta;
        } catch (error) {
            console.error('ContentService: createSnippet failed', error);
            throw error;
        }
    },
    subscribeToIngestStream(job: ContentIngestJobMeta, handlers: IngestStreamHandlers = {}): () => void {
        if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
            console.warn('ContentService: EventSource not available in current environment');
            return () => undefined;
        }

        try {
            const existingSource = ingestEventSources.get(job.jobId);
            if (existingSource) {
                existingSource.close();
                ingestEventSources.delete(job.jobId);
            }

            console.log('ContentService: Connecting to SSE stream with token:', {
                streamPath: job.streamPath,
                hasToken: Boolean(job.streamToken)
            });

            const connectionUrl = buildStreamConnectionUrl(job.streamUrl, job.streamToken);
            const eventSource = new EventSource(connectionUrl, { withCredentials: false });
            ingestEventSources.set(job.jobId, eventSource);

            eventSource.onopen = () => {
                console.log('ContentService: SSE connection opened');
                handlers.onOpen?.();
            };

            eventSource.addEventListener('heartbeat', (event: MessageEvent<string>) => {
                handlers.onHeartbeat?.(event);
            });

            eventSource.onmessage = (event: MessageEvent<string>) => {
                try {
                    const payload = JSON.parse(event.data) as ContentIngestEvent;
                    handlers.onEvent?.(payload);

                    if (payload.type === 'error') {
                        handlers.onError?.(payload);
                        eventSource.close();
                        ingestEventSources.delete(job.jobId);
                        return;
                    }

                    if (payload.type === 'status' && (payload as ContentIngestStatusEvent).stage === 'ready') {
                        handlers.onComplete?.(payload);
                        eventSource.close();
                        ingestEventSources.delete(job.jobId);
                    }
                } catch (parseError) {
                    console.error('ContentService: Failed to parse ingest SSE event', parseError, event.data);
                }
            };

            eventSource.onerror = (event: Event) => {
                console.error('ContentService: Ingest SSE connection error', event);
                const errorEvent: ContentIngestErrorEvent = {
                    type: 'error',
                    job_id: job.jobId,
                    message: 'Stream connection error'
                };
                handlers.onError?.(errorEvent);
                eventSource.close();
                ingestEventSources.delete(job.jobId);
            };

            return () => {
                eventSource.close();
                ingestEventSources.delete(job.jobId);
            };
        } catch (connectionError) {
            console.error('ContentService: Failed to open SSE stream', connectionError);
            const errorEvent: ContentIngestErrorEvent = {
                type: 'error',
                job_id: job.jobId,
                message: 'Unable to connect to ingestion stream'
            };
            handlers.onError?.(errorEvent);
            return () => undefined;
        }
    },
    closeIngestStream(jobId: string) {
        const source = ingestEventSources.get(jobId);
        if (source) {
            source.close();
            ingestEventSources.delete(jobId);
        }
    },
    async createFile(content: Content): Promise<ContentIngestJobMeta> {
        if (!content.file) {
            throw new Error('File is required for file upload');
        }

        const chatbotIds = normalizeIdArray(content.chatbots);
        const copilotIds = (content.copilots ?? []).filter((id): id is string => Boolean(id));
        const tags = (content.tags ?? []).filter((id): id is string => Boolean(id));
        const formData = new FormData();

        // Basic file and title
        formData.append('file', content.file);
        formData.append('title', content.title);

        // Folder ID (only if specified and not 'all')
        if (content.folderId && content.folderId !== 'all') {
            formData.append('folder_id', content.folderId);
        }

        // Metadata as JSON string
        const metadata = {
            source: "file_upload",
            category: content.category || content.description || "documentation",
            department: "general" // Default department
        };
        formData.append('metadata', JSON.stringify(metadata));

        // Tags as JSON array string
        if (tags.length > 0) {
            formData.append('tags_id', JSON.stringify(tags));
        }

        // Chatbots as JSON array string
        if (chatbotIds.length > 0) {
            console.log('ContentService: Adding chatbots to file upload:', chatbotIds);
            formData.append('chatbot_id', JSON.stringify(chatbotIds));
        }

        // Copilots as JSON array string
        if (copilotIds.length > 0) {
            formData.append('copilot_id', JSON.stringify(copilotIds));
        }

        // Processing options as JSON string
        const processingOptions = buildProcessingOptions(content.processingOptions);
        formData.append('processing_options', JSON.stringify(processingOptions));

        console.log('ContentService: Creating file with ingest API', {
            metadata,
            tags,
            chatbotIds,
            copilotIds,
            processingOptions
        });

        try {
            const response = await HttpClient.apiClient.post<RawIngestResponse>('/ingest/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000
            });
            const jobMeta = normalizeIngestResponse(response.data, 'file', {
                title: content.title,
                folderId: content.folderId ?? undefined,
                tags,
                chatbots: chatbotIds,
                copilots: copilotIds,
                type: 'file'
            });

            return jobMeta;
        } catch (error) {
            console.error('ContentService: createFile failed', error);
            throw error;
        }
    },
    async createWebsite(content: Content): Promise<ContentIngestJobMeta> {
        // Flexible URL field detection - check multiple possible field names
        const url = content.url || content.content || content.link || content.website_url;

        if (!url) {
            throw new Error('URL is required for website ingestion');
        }

        console.log('ContentService: Detected URL from content:', url, 'Original content:', content);

        const tags = content.tags ?? [];
        const folderId = content.folderId && content.folderId !== 'all' ? content.folderId : undefined;
        const chatbotIds = normalizeIdArray(content.chatbots);
        const copilotIds = (content.copilots ?? []).filter((id): id is string => Boolean(id));

        // Transform to new URL mapping API payload structure
        // URL mapping parameters are nested under "request" object
        const urlMappingPayload = {
            request: {
                start_url: url,
                max_depth: content.maxDepth || 2,              // Default crawl depth
                max_urls: content.maxUrls || 10,               // Default max URLs
                same_domain_only: content.sameDomainOnly !== false, // Default true
                include_patterns: content.includePatterns || ["**"], // Default include all
                exclude_patterns: content.excludePatterns || []      // Default exclude none
            },
            // Metadata fields remain at root level
            tags_id: tags,
            folder_id: folderId,
            chatbot_id: chatbotIds,
            copilot_id: copilotIds
        };

        // Remove undefined values to keep payload clean
        Object.keys(urlMappingPayload).forEach(key => {
            if (urlMappingPayload[key] === undefined) {
                delete urlMappingPayload[key];
            }
        });

        console.log('ContentService: Creating website with URL mapping API:', urlMappingPayload);

        try {
            const response = await HttpClient.apiClient.post<RawIngestResponse>('/v1/ingest/map-urls', urlMappingPayload, {
                timeout: 60000
            });

            const jobMeta = normalizeIngestResponse(response.data as RawIngestResponse, 'website', {
                title: content.title,
                folderId,
                tags,
                chatbots: chatbotIds,
                copilots: copilotIds,
                type: 'website'
            });

            return jobMeta;
        } catch (error) {
            console.error('ContentService: createWebsite failed', error);
            throw error;
        }
    },
    async deleteContent(contentIds: string[]): Promise<void> {
        if (!contentIds?.length) {
            throw new Error('At least one content ID is required for deletion.');
        }

        try {
            await HttpClient.apiClient.delete('/v1/ingest/content', {
                data: { content_ids: contentIds }
            });
        } catch (error) {
            console.error('ContentService: deleteContent failed', error);
            throw error;
        }
    },
    async uploadFile(file: File): Promise<Content> {
        const response = await HttpClient.apiClient.post<Content>(API_URL + '/upload', file);
        return response.data;
    },
    async fetchContent(params?: Record<string, any>): Promise<ContentListResult> {
        try {
            const requestParams = {
                page: params?.page ?? 1,
                limit: params?.limit ?? 20,
                ...params
            };

            const response = await HttpClient.apiClient.get('/v1/ingest/content', { params: requestParams });
            const payload = response.data;
            return normalizeContentListResponse(payload);
        } catch (error) {
            console.error('ContentService: Error fetching content list', error);
            throw error;
        }
    },
    async linkChatbots({
        contentIds,
        chatbotIds,
    }: {
        contentIds: string[];
        chatbotIds: string[];
    }) {
        if (!contentIds?.length || !chatbotIds?.length) {
            throw new Error('Both content IDs and chatbot IDs are required');
        }

        try {
            const response = await HttpClient.apiClient.post('/ingest/link-chatbots', {
                content_ids: contentIds,
                chatbot_ids: chatbotIds,
            });
            return response.data;
        } catch (error) {
            console.error('ContentService: linkChatbots failed', error);
            throw error;
        }
    },
    async linkCopilot({
        contentIds,
        copilotProfileId,
    }: {
        contentIds: string[];
        copilotProfileId: string;
    }) {
        if (!contentIds?.length) {
            throw new Error('At least one content ID is required');
        }
        if (!copilotProfileId) {
            throw new Error('Copilot profile ID is required');
        }

        try {
            const response = await HttpClient.apiClient.post('/ingest/link-copilot', {
                content_ids: contentIds,
                copilot_profile_id: copilotProfileId,
            });
            return response.data;
        } catch (error) {
            console.error('ContentService: linkCopilot failed', error);
            throw error;
        }
    },
    async assignContentMetadata({
        contentIds,
        chatbotIds,
        copilotIds,
        operation = 'append'
    }: {
        contentIds: string[];
        chatbotIds?: string[];
        copilotIds?: string[];
        operation?: 'append' | 'replace';
    }) {
        if (!contentIds?.length) {
            throw new Error('At least one content ID is required to assign metadata.');
        }

        if ((!chatbotIds || chatbotIds.length === 0) && (!copilotIds || copilotIds.length === 0)) {
            throw new Error('Provide at least one chatbot or copilot ID when assigning metadata.');
        }

        const payload = {
            content_ids: contentIds,
            chatbot_ids: chatbotIds ?? [],
            copilot_ids: copilotIds ?? [],
            operation
        };

        try {
            const response = await HttpClient.apiClient.post('/v1/ingest/content/assign-metadata', payload);
            return response.data;
        } catch (error) {
            console.error('ContentService: assignContentMetadata failed', error);
            throw error;
        }
    },
    async fetchContentById(id: string): Promise<Content> {
        try {
            const response = await HttpClient.apiClient.get(`${API_URL}/${id}`);
            const payload = response.data?.data ?? response.data;

            if (!payload) {
                throw new Error('Content not found');
            }

            return normalizeContentRecord(payload);
        } catch (primaryError) {
            console.warn('ContentService: Primary content detail API failed, attempting ingest fallback', primaryError);

            try {
                const response = await HttpClient.apiClient.get(`/v1/ingest/content/${id}`);
                const payload = response.data;
                const contentData = payload?.data ?? payload;

                if (!contentData) {
                    throw new Error('Content not found');
                }

                return normalizeContentRecord(contentData);
            } catch (error) {
                console.error('ContentService: Error fetching content by ID:', id, error);
                throw error;
            }
        }
    },
    // Get all content
    getContentList: async (params?: any): Promise<any> => {
        const response = await api.get('/content', { params });
        return response.data;
    },
    // Create new content
    createContent: async (contentData: Omit<Content, 'id' | 'createdAt' | 'updatedAt'>): Promise<any> => {
        const formData = new FormData();
        
        // Add basic fields
        formData.append('title', contentData.title);
        formData.append('content', contentData.content);
        formData.append('type', contentData.type);
        formData.append('status', contentData.status);
        
        if (contentData.description) {
            formData.append('description', contentData.description);
        }
        
        if (contentData.folderId) {
            formData.append('folderId', contentData.folderId);
        }
        
        if (contentData.tags && contentData.tags.length > 0) {
            contentData.tags.forEach(tag => {
                formData.append('tags[]', tag);
            });
        }
        
        if (contentData.chatbots && contentData.chatbots.length > 0) {
            contentData.chatbots.forEach(chatbot => {
                formData.append('chatbots[]', chatbot);
            });
        }
        
        if (contentData.copilots && contentData.copilots.length > 0) {
            contentData.copilots.forEach(copilot => {
                formData.append('copilots[]', copilot);
            });
        }
        
        // Add file if present
        if (contentData.file) {
            formData.append('file', contentData.file);
        }
        
        const response = await api.post('/content', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }
};
